import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, Pagination, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import axios from 'axios';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';
import { findTalentUrl, influencersSearchFiltersUrl } from '../../configurations/api/url';
import type { IInfluencer } from '../../redux/buzzbridge/types';
import { CreatorPublicProfileDrawer } from '../../components/PublicInfluencerProfile/CreatorPublicProfileDrawer';
import { getInfluencerAccountUserName } from '../../components/PublicInfluencerProfile/publicInfluencerProfileUtils';
import type { CampaignType } from './brandCampaignConfig';
import type { AuditionCuratedFilterSnapshot } from './BrandNewAuditionForm';
import type { UgcCuratedFilterSnapshot } from './BrandNewUGCCampaignForm';
import type { ITalentProfile } from './BrandTalentTab';

/** Stable fallback so `?? []` does not allocate a new array every render (react-hooks/exhaustive-deps). */
const EMPTY_ROLE_GIG_TYPES: string[] = [];

interface InfluencerFilterState {
    search: string;
    gender: string;
    country: string;
    category: string;
    sub_category: string;
    platform: string;
    min_followers: string;
    max_followers: string;
    available_for_events?: boolean;
    available_for_long_term_collabs?: boolean;
    /** Soft-rank featured → location → category (no hard exclude). */
    suggestion: boolean;
    page: number;
}

const DEFAULT_INFLUENCER_FILTERS: InfluencerFilterState = {
    search: '',
    gender: '',
    country: '',
    category: '',
    sub_category: '',
    platform: '',
    min_followers: '',
    max_followers: '',
    suggestion: true,
    page: 1,
};

const PAGE_LIMIT = 20;

function buildInfluencerParams(filters: InfluencerFilterState): URLSearchParams {
    const p = new URLSearchParams();
    if (filters.search) p.set('search', filters.search);
    if (filters.gender) p.set('gender', filters.gender);
    if (filters.country) p.set('country', filters.country);
    if (filters.category) p.set('category', filters.category);
    if (filters.sub_category) p.set('sub_category', filters.sub_category);
    if (filters.platform) {
        p.set('platform', filters.platform);
        if (filters.min_followers) p.set('min_followers', filters.min_followers);
        if (filters.max_followers) p.set('max_followers', filters.max_followers);
    }
    if (filters.available_for_events !== undefined) p.set('available_for_events', String(filters.available_for_events));
    if (filters.available_for_long_term_collabs !== undefined)
        p.set('available_for_long_term_collabs', String(filters.available_for_long_term_collabs));
    if (filters.suggestion) p.set('suggestion', 'true');
    p.set('page', String(filters.page));
    p.set('limit', String(PAGE_LIMIT));
    return p;
}

/** Aligns with FindTalentsContainer TYPE_TABS (`profile_type`) for discover / audme-style talent browse. */
export function auditionGigTypeToProfileType(gigType: string): string | undefined {
    const g = (gigType || '').trim().toLowerCase();
    if (g === 'cast') return 'performer';
    if (g === 'crew') return 'technical';
    if (g === 'technical') return 'content_creator';
    if (g === 'mixed' || g === 'all' || !g) return undefined;
    return undefined;
}

/** Maps audition + per-role gig types to discover `profile_type` values (performer | technical | content_creator). */
export function profileTypesFromAuditionAndRoles(auditionGigType: string, roleGigTypes: string[]): string[] {
    const g = (auditionGigType || '').trim().toLowerCase();
    if (g === 'mixed') {
        const mapped = roleGigTypes
            .map(rt => auditionGigTypeToProfileType(rt.trim()))
            .filter((x): x is string => Boolean(x));
        return [...new Set(mapped)];
    }
    const one = auditionGigTypeToProfileType(g);
    return one ? [one] : [];
}

/** Single `profile_type` query param when the API accepts one; multiple distinct types → omit (client refines). */
export function discoverProfileTypeApiParam(auditionGigType: string, roleGigTypes: string[]): string | undefined {
    const types = profileTypesFromAuditionAndRoles(auditionGigType, roleGigTypes);
    if (types.length === 1) return types[0];
    return undefined;
}

function talentMatchesCuratedProfileTypes(profile: ITalentProfile, allowed: string[]): boolean {
    if (!allowed.length) return true;
    const pt = (profile.profile_type || '').trim().toLowerCase();
    if (!pt) return true;
    return allowed.some(exp => {
        const e = exp.toLowerCase();
        if (pt === e) return true;
        if (e === 'performer') return pt.includes('perform') || pt.includes('actor') || pt.includes('model');
        if (e === 'technical') return pt.includes('crew') || pt.includes('technical');
        if (e === 'content_creator') return pt.includes('content') || pt.includes('creator') || pt.includes('editor');
        return pt.includes(e);
    });
}

/** Parse min/max age strings for find-talent query (matches BrandTalentTab). */
function parseAgeQueryParams(minStr: string, maxStr: string): { ageFrom: string; ageTo: string } {
    const mn = parseInt(String(minStr ?? '').trim(), 10);
    const mx = parseInt(String(maxStr ?? '').trim(), 10);
    const hasMin = Number.isFinite(mn);
    const hasMax = Number.isFinite(mx);
    if (!hasMin && !hasMax) return { ageFrom: '', ageTo: '' };
    if (hasMin && hasMax) {
        const lo = Math.min(mn, mx);
        const hi = Math.max(mn, mx);
        return { ageFrom: String(lo), ageTo: String(hi) };
    }
    if (hasMin) return { ageFrom: String(mn), ageTo: '' };
    return { ageFrom: '', ageTo: String(mx) };
}

function talentProfileAgeYears(profile: ITalentProfile): number | undefined {
    if (typeof profile.age === 'number' && Number.isFinite(profile.age)) return profile.age;
    const rec = profile as unknown as Record<string, unknown>;
    const raw = rec.age_years;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
        const n = parseInt(raw, 10);
        return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
}

/** Extra client pass when age bounds are set (API may already narrow). */
function talentMatchesAgeClient(profile: ITalentProfile, minStr: string, maxStr: string): boolean {
    const { ageFrom, ageTo } = parseAgeQueryParams(minStr, maxStr);
    if (!ageFrom && !ageTo) return true;
    const age = talentProfileAgeYears(profile);
    if (age == null) return true;
    if (ageFrom && age < Number(ageFrom)) return false;
    if (ageTo && age > Number(ageTo)) return false;
    return true;
}

function talentMatchesLocationHint(profile: ITalentProfile, locationHint: string): boolean {
    const hint = (locationHint || '').trim().toLowerCase();
    if (!hint) return true;
    const addr = (profile.location?.address || '').toLowerCase();
    if (!addr) return false;
    const tokens = hint.split(/[\s,]+/).filter(p => p.length > 1);
    if (tokens.length === 0) return addr.includes(hint);
    return tokens.some(part => addr.includes(part));
}

function getTalentDisplayName(p: ITalentProfile): string {
    const stage = p.stage_name?.trim();
    if (stage) return stage;
    const u = p.user;
    const n = [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
    return n || p.title?.trim() || 'Unknown';
}

function getTalentAvatarUrl(p: ITalentProfile): string | undefined {
    return p.profile_picture || p.user?.profile_picture || p.photos?.[0];
}

function getTalentSubtitle(p: ITalentProfile): string {
    const title = p.title?.trim();
    const skills = Array.isArray(p.skills)
        ? p.skills
              .map(s => (typeof s === 'object' && s && 'skill' in s ? (s as { skill?: string }).skill : ''))
              .filter(Boolean)
              .slice(0, 4)
              .join(', ')
        : '';
    return title || skills || p.location?.address || '—';
}

/** Thspian user id for audition invite API (`invite-user` body `userId`). */
export function getCuratedTalentInviteUserId(p: ITalentProfile): string {
    return (p.user?._id || p.created_by?._id || '').trim();
}

export interface BrandCampaignCuratedCreatorsPanelProps {
    campaignType: CampaignType;
    /** When campaign is Creative Job, drives talent discover + client filters. */
    auditionCurated?: AuditionCuratedFilterSnapshot;
    /** UGC form location/category — soft-ranks curated influencers. */
    ugcCurated?: UgcCuratedFilterSnapshot;
    invitedIds: Set<string>;
    onToggleInvite: (id: string) => void;
}

export function BrandCampaignCuratedCreatorsPanel({
    campaignType,
    auditionCurated,
    ugcCurated,
    invitedIds,
    onToggleInvite,
}: BrandCampaignCuratedCreatorsPanelProps) {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const isTalentMode = campaignType === 'audition';

    const [influencerFilters, setInfluencerFilters] = useState<InfluencerFilterState>(DEFAULT_INFLUENCER_FILTERS);
    const [searchInput, setSearchInput] = useState('');
    const [creators, setCreators] = useState<IInfluencer[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [profileTarget, setProfileTarget] = useState<IInfluencer | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const [talentSearchInput, setTalentSearchInput] = useState('');
    const [talentSearch, setTalentSearch] = useState('');
    const [talentPage, setTalentPage] = useState(1);
    const [rawTalents, setRawTalents] = useState<ITalentProfile[]>([]);
    const [talentTotalPages, setTalentTotalPages] = useState(1);

    const shootLoc = (auditionCurated?.shoot_location ?? '').trim();
    const effectiveTalentLocation = shootLoc;
    const minAgeStr = auditionCurated?.min_age ?? '';
    const maxAgeStr = auditionCurated?.max_age ?? '';
    const { ageFrom: apiAgeFrom, ageTo: apiAgeTo } = parseAgeQueryParams(minAgeStr, maxAgeStr);
    const gigTypeForApi = auditionCurated?.gig_type ?? '';
    const roleGigTypes = auditionCurated?.role_gig_types ?? EMPTY_ROLE_GIG_TYPES;
    const roleGigTypesKey = [...roleGigTypes].sort().join('\u0001');

    const clientProfileTypes = useMemo(
        () => profileTypesFromAuditionAndRoles(gigTypeForApi, roleGigTypes),
        [gigTypeForApi, roleGigTypes],
    );

    const filteredTalents = useMemo(() => {
        if (!isTalentMode) return [];
        return rawTalents.filter(
            p =>
                talentMatchesLocationHint(p, effectiveTalentLocation) &&
                talentMatchesCuratedProfileTypes(p, clientProfileTypes) &&
                talentMatchesAgeClient(p, minAgeStr, maxAgeStr),
        );
    }, [isTalentMode, rawTalents, effectiveTalentLocation, clientProfileTypes, minAgeStr, maxAgeStr]);

    const fetchInfluencers = useCallback(async (activeFilters: InfluencerFilterState) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        try {
            const params = buildInfluencerParams(activeFilters);
            const res = await axios.get(`${influencersSearchFiltersUrl}?${params.toString()}`, {
                signal: controller.signal,
            });

            if (!controller.signal.aborted) {
                const payload = res.data;
                const list: IInfluencer[] = payload?.data?.influencers ?? payload?.data ?? payload?.influencers ?? [];
                setCreators(Array.isArray(list) ? list : []);
                setTotalPages(payload?.totalPages ?? 1);
                setTotalCount(payload?.totalCount ?? list.length);
            }
        } catch (err: any) {
            if (!axios.isCancel(err)) {
                setCreators([]);
                setTotalPages(1);
                setTotalCount(0);
            }
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, []);

    const fetchTalents = useCallback(
        async (opts: {
            search: string;
            page: number;
            gigType: string;
            roleGigTypes: string[];
            location: string;
            ageFrom: string;
            ageTo: string;
        }) => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (opts.search) params.set('searchTerm', opts.search);
                const profileType = discoverProfileTypeApiParam(opts.gigType, opts.roleGigTypes);
                if (profileType) params.set('profile_type', profileType);
                if (opts.location) params.set('location', opts.location);
                if (opts.ageFrom) params.set('ageFrom', opts.ageFrom);
                if (opts.ageTo) params.set('ageTo', opts.ageTo);
                params.set('page', String(opts.page));
                params.set('limit', String(PAGE_LIMIT));

                const res = await axios.get(`${findTalentUrl}?${params.toString()}`, {
                    signal: controller.signal,
                });

                if (!controller.signal.aborted) {
                    const payload = res.data;
                    const list: ITalentProfile[] = payload?.data ?? payload?.profiles ?? payload?.results ?? [];
                    setRawTalents(Array.isArray(list) ? list : []);
                    setTalentTotalPages(payload?.totalPages ?? 1);
                }
            } catch (err: any) {
                if (!axios.isCancel(err)) {
                    setRawTalents([]);
                    setTalentTotalPages(1);
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        },
        [],
    );

    // Influencer: debounce search → filters
    useEffect(() => {
        if (isTalentMode) return;
        const timer = setTimeout(() => {
            setInfluencerFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput, isTalentMode]);

    // UGC form soft filters: featured first, then location (if not worldwide), then category/sub.
    useEffect(() => {
        if (isTalentMode) return;
        const worldWide = Boolean(ugcCurated?.world_wide);
        const country = !worldWide ? String(ugcCurated?.country || '').trim() : '';
        const category = String(ugcCurated?.category || '').trim();
        const sub_category = String(ugcCurated?.sub_category || '').trim();
        setInfluencerFilters(prev => {
            if (
                prev.country === country &&
                prev.category === category &&
                prev.sub_category === sub_category &&
                prev.suggestion
            ) {
                return prev;
            }
            return {
                ...prev,
                country,
                category,
                sub_category,
                suggestion: true,
                page: 1,
            };
        });
    }, [isTalentMode, ugcCurated?.world_wide, ugcCurated?.country, ugcCurated?.category, ugcCurated?.sub_category]);

    useEffect(() => {
        if (isTalentMode) return;
        fetchInfluencers(influencerFilters);
    }, [influencerFilters, fetchInfluencers, isTalentMode]);

    // Talent: debounce search (same cadence as BrandTalentTab)
    useEffect(() => {
        if (!isTalentMode) return;
        const timer = setTimeout(() => {
            setTalentSearch(talentSearchInput);
            setTalentPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [talentSearchInput, isTalentMode]);

    useLayoutEffect(() => {
        if (!isTalentMode) return;
        setTalentPage(1);
    }, [isTalentMode, shootLoc, minAgeStr, maxAgeStr, gigTypeForApi, roleGigTypesKey]);

    useEffect(() => {
        if (!isTalentMode) return;
        fetchTalents({
            search: talentSearch,
            page: talentPage,
            gigType: gigTypeForApi,
            roleGigTypes,
            location: effectiveTalentLocation,
            ageFrom: apiAgeFrom,
            ageTo: apiAgeTo,
        });
    }, [
        talentSearch,
        talentPage,
        gigTypeForApi,
        roleGigTypes,
        effectiveTalentLocation,
        apiAgeFrom,
        apiAgeTo,
        fetchTalents,
        isTalentMode,
    ]);

    useEffect(() => {
        return () => abortRef.current?.abort();
    }, []);

    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
        if (isTalentMode) setTalentPage(page);
        else setInfluencerFilters(prev => ({ ...prev, page }));
    };

    const headerTitle = isTalentMode ? 'Curated talents' : 'Curated creators';
    const searchPlaceholder = isTalentMode
        ? 'Search talent by name or services...'
        : 'Search by name, bio, or username...';

    const foundCount = isTalentMode ? filteredTalents.length : totalCount;
    const pageCount = isTalentMode ? talentTotalPages : totalPages;
    const activePage = isTalentMode ? talentPage : influencerFilters.page;

    return (
        <Box
            id="curated-creators-panel"
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER,
                borderRadius: '24px',
                p: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: 'column',
                height: { xs: 460, sm: 520, lg: 700 },
                maxHeight: { xs: '65vh', sm: '70vh', lg: 700 },
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.05)',
                minWidth: 0,
            }}>
            <Box sx={{ mb: 3, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, fontWeight: 800, color: t.primary_white }}>
                        {headerTitle}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            bgcolor: 'rgba(0,253,193,0.1)',
                            color: BRAND_LUXE_SECONDARY,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                        }}>
                        {loading ? '…' : `${foundCount} FOUND`}
                    </Typography>
                </Box>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={searchPlaceholder}
                    value={isTalentMode ? talentSearchInput : searchInput}
                    onChange={e =>
                        isTalentMode ? setTalentSearchInput(e.target.value) : setSearchInput(e.target.value)
                    }
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: t.form_label, fontSize: 22 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            bgcolor: '#262626',
                            borderRadius: 2,
                            fontSize: 14,
                            color: t.primary_white,
                            '& fieldset': { border: 'none' },
                            '&:hover fieldset': { border: 'none' },
                            '&.Mui-focused fieldset': { border: 'none' },
                            '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(236,170,0,0.35)' },
                        },
                        '& .MuiInputBase-input': { color: t.primary_white },
                        '& .MuiInputBase-input::placeholder': { color: t.form_label, opacity: 1 },
                    }}
                />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    pr: 0.5,
                    mr: -0.5,
                    '&::-webkit-scrollbar': { width: 0, height: 0 },
                }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_LUXE_PRIMARY }} />
                    </Box>
                ) : isTalentMode ? (
                    filteredTalents.length === 0 ? (
                        <Box sx={{ textAlign: 'center', pt: 6 }}>
                            <Typography sx={{ fontSize: 13, color: t.form_label }}>
                                {talentSearch
                                    ? 'No talents matched your search and filters.'
                                    : 'No talents matched these filters yet.'}
                            </Typography>
                        </Box>
                    ) : (
                        filteredTalents.map(p => {
                            const profileId = p._id ?? '';
                            const inviteUserId = getCuratedTalentInviteUserId(p);
                            const invited = Boolean(inviteUserId && invitedIds.has(inviteUserId));
                            const avatar = getTalentAvatarUrl(p);
                            const subtitle = getTalentSubtitle(p);
                            return (
                                <Box
                                    key={`${profileId}-${inviteUserId}`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 2,
                                        borderRadius: 4,
                                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                        mb: 2,
                                        minWidth: 0,
                                    }}>
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                            bgcolor: '#262626',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        {avatar ? (
                                            <Box
                                                component="img"
                                                src={avatar}
                                                alt=""
                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <Typography sx={{ fontSize: 20, color: t.form_label }}>
                                                {getTalentDisplayName(p)[0]?.toUpperCase() ?? '?'}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 16,
                                                color: t.primary_white,
                                            }}
                                            noWrap>
                                            {getTalentDisplayName(p)}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: t.form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}
                                            noWrap>
                                            {subtitle}
                                        </Typography>
                                    </Box>
                                    {invited ? (
                                        <Button
                                            size="small"
                                            onClick={e => {
                                                e.stopPropagation();
                                                if (inviteUserId) onToggleInvite(inviteUserId);
                                            }}
                                            startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                                            sx={{
                                                flexShrink: 0,
                                                borderRadius: 999,
                                                textTransform: 'none',
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 13,
                                                bgcolor: 'rgba(0,253,193,0.2)',
                                                color: BRAND_LUXE_SECONDARY,
                                                border: '1px solid rgba(0,253,193,0.2)',
                                            }}>
                                            Invited
                                        </Button>
                                    ) : (
                                        <Button
                                            size="small"
                                            disabled={!inviteUserId}
                                            title={
                                                inviteUserId
                                                    ? undefined
                                                    : 'This profile is not linked to a user account yet.'
                                            }
                                            onClick={e => {
                                                e.stopPropagation();
                                                if (inviteUserId) onToggleInvite(inviteUserId);
                                            }}
                                            sx={{
                                                flexShrink: 0,
                                                borderRadius: 999,
                                                textTransform: 'none',
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 13,
                                                minWidth: 72,
                                                color: '#392700',
                                                bgcolor: BRAND_LUXE_PRIMARY,
                                                '&:hover': { bgcolor: '#feb700' },
                                            }}>
                                            Invite
                                        </Button>
                                    )}
                                </Box>
                            );
                        })
                    )
                ) : creators.length === 0 ? (
                    <Box sx={{ textAlign: 'center', pt: 6 }}>
                        <Typography sx={{ fontSize: 13, color: t.form_label }}>
                            {influencerFilters.search ? 'No creators matched your search.' : 'No creators available.'}
                        </Typography>
                    </Box>
                ) : (
                    creators.map(c => {
                        const id = c._id ?? '';
                        const invited = invitedIds.has(id);
                        const niches = Array.isArray(c.categories)
                            ? c.categories
                                  .map((cat: any) => (typeof cat === 'string' ? cat : cat?.name ?? cat?.main ?? ''))
                                  .filter(Boolean)
                                  .join(', ')
                            : '';
                        const pastWorkCount = c.past_work?.length ?? 0;
                        return (
                            <Box
                                key={id}
                                onClick={() => setProfileTarget(c)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2,
                                    borderRadius: 4,
                                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                    mb: 2,
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                    '&:hover': { bgcolor: BRAND_SURFACE_CONTAINER_HIGH },
                                    minWidth: 0,
                                }}>
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                        bgcolor: '#262626',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    {c.profile_picture ? (
                                        <Box
                                            component="img"
                                            src={c.profile_picture}
                                            alt=""
                                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <Typography sx={{ fontSize: 20, color: t.form_label }}>
                                            {(c.name ?? '?')[0].toUpperCase()}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 16,
                                            color: t.primary_white,
                                        }}
                                        noWrap>
                                        {c.name ?? 'Unknown'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: t.form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}
                                            noWrap>
                                            {niches || c.country || '—'}
                                        </Typography>
                                        {pastWorkCount > 0 && (
                                            <Typography
                                                sx={{
                                                    fontSize: 10,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: BRAND_LUXE_PRIMARY,
                                                    bgcolor: 'rgba(255,201,101,0.1)',
                                                    px: 0.75,
                                                    py: 0.25,
                                                    borderRadius: 0.75,
                                                    flexShrink: 0,
                                                    letterSpacing: '0.04em',
                                                }}>
                                                {pastWorkCount} work{pastWorkCount > 1 ? 's' : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                {invited ? (
                                    <Button
                                        size="small"
                                        onClick={e => {
                                            e.stopPropagation();
                                            onToggleInvite(id);
                                        }}
                                        startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                                        sx={{
                                            flexShrink: 0,
                                            borderRadius: 999,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 13,
                                            bgcolor: 'rgba(0,253,193,0.2)',
                                            color: BRAND_LUXE_SECONDARY,
                                            border: '1px solid rgba(0,253,193,0.2)',
                                        }}>
                                        Invited
                                    </Button>
                                ) : (
                                    <Button
                                        size="small"
                                        onClick={e => {
                                            e.stopPropagation();
                                            onToggleInvite(id);
                                        }}
                                        sx={{
                                            flexShrink: 0,
                                            borderRadius: 999,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 13,
                                            minWidth: 72,
                                            color: '#392700',
                                            bgcolor: BRAND_LUXE_PRIMARY,
                                            '&:hover': { bgcolor: '#feb700' },
                                        }}>
                                        Invite
                                    </Button>
                                )}
                            </Box>
                        );
                    })
                )}
            </Box>

            {!loading && pageCount > 1 && !isTalentMode && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, flexShrink: 0 }}>
                    <Pagination
                        count={pageCount}
                        page={activePage}
                        onChange={handlePageChange}
                        size="small"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: t.form_label,
                                fontSize: 12,
                            },
                            '& .MuiPaginationItem-root.Mui-selected': {
                                bgcolor: BRAND_LUXE_PRIMARY,
                                color: '#392700',
                            },
                        }}
                    />
                </Box>
            )}
            {!loading && pageCount > 1 && isTalentMode && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, flexShrink: 0 }}>
                    <Pagination
                        count={pageCount}
                        page={activePage}
                        onChange={handlePageChange}
                        size="small"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: t.form_label,
                                fontSize: 12,
                            },
                            '& .MuiPaginationItem-root.Mui-selected': {
                                bgcolor: BRAND_LUXE_PRIMARY,
                                color: '#392700',
                            },
                        }}
                    />
                </Box>
            )}
            <CreatorPublicProfileDrawer
                open={Boolean(profileTarget)}
                onClose={() => setProfileTarget(null)}
                userName={profileTarget ? getInfluencerAccountUserName(profileTarget) : undefined}
                initialInfluencer={profileTarget}
                onInvite={() => {
                    if (profileTarget?._id) onToggleInvite(profileTarget._id);
                    setProfileTarget(null);
                }}
                inviteLabel="Invite"
                invited={profileTarget ? invitedIds.has(profileTarget._id ?? '') : false}
            />
        </Box>
    );
}
